import { makeModuleApiInfoSelector } from "@client/content/selectors";
import { entitiesSelector, makeModuleEntitySelector } from "@client/selectors";
import { type BaseAppState, type EntityRef, type OptionItem } from "@client/types";
import { type HttpApiResponse } from "@client/types/Api";
import { type Dispatch } from "redux";
import { initAction } from "../actions";
import api from "./api";
import { localStateSelector, makeLocalStateItemSelector } from "./selectors";
import { type ReportModule } from "./types";

export enum Actions {
  InitBegin = "REPORT_MODULE_INIT_BEGIN",
  InitFailure = "REPORT_MODULE_INIT_FAILURE",
  InitSuccess = "REPORT_MODULE_INIT_SUCCESS",

  SetItemId = "REPORT_MODULE_SET_REPORT_ID"
}

export type InitBeginActionData = EntityRef;
export type InitSuccessActionData = {
  itemId: string | null;
  reportOptions: OptionItem<string>[];
};
export const _init = initAction<
  BaseAppState,
  InitBeginActionData,
  ReportModule.Api.InitResponse,
  InitSuccessActionData
>({
  types: {
    begin: Actions.InitBegin,
    failure: Actions.InitFailure,
    success: Actions.InitSuccess
  },
  action: async (
    state: BaseAppState,
    dispatch: any,
    params: InitBeginActionData
  ): Promise<HttpApiResponse<ReportModule.Api.InitResponse>> => {
    const apiInfo = makeModuleApiInfoSelector()(state, params);

    return api.init(apiInfo, {});
  },
  mapResponse: (
    state: BaseAppState,
    resp: ReportModule.Api.InitResponse,
    args: InitBeginActionData
  ): InitSuccessActionData => {
    const module = makeModuleEntitySelector()(state, { id: args.id, source: args.source });
    return {
      ...resp,
      reportOptions: resp.reportOptions.map(({ caption, ...x }) => ({ ...x, label: caption })),
      itemId: (module.childrenIds ?? [])[0] ?? null
    };
  }
});

export type InitArgs = EntityRef;
export const init = (args: InitArgs) => (dispatch: Dispatch<any>, getState: () => BaseAppState) => {
  const state = getState();
  const localState = localStateSelector(state, args);
  if (localState.status === "None") {
    dispatch(_init(args));
  }
};

export type SetItemIdActionData = EntityRef & {
  itemId: string | null;
  isDifferentReport: boolean;
};
export const setItemId =
  (ref: EntityRef, itemId: string | null) => (dispatch: Dispatch<any>, getState: () => BaseAppState) => {
    const state = getState();
    const currentItem = makeLocalStateItemSelector()(state, ref);
    const entities = entitiesSelector(state, { source: ref.source });
    const newItem = itemId ? entities[itemId] : null;
    const isDifferentReport =
      !!newItem &&
      (currentItem?.data as ReportModule.ModuleItemData).reportId !==
        (newItem.data as ReportModule.ModuleItemData).reportId;

    dispatch({
      type: Actions.SetItemId,
      data: {
        ...ref,
        itemId,
        isDifferentReport
      }
    });
  };
